import './App.css';
import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import SplashScreen from './components/SplashScreen';
import { AppProvider, useApp } from './context/AppContext';
import BottomNav from './components/BottomNav';
import AuthModal from './components/AuthModal';
import EmailVerificationScreen from './components/EmailVerificationScreen';
import ForYouPage from './pages/ForYouPage';
import SearchPage from './pages/SearchPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import PostJobPage from './pages/PostJobPage';
import InsertePage from './pages/InsertePage';
import TalentePage from './pages/TalentePage';
import WelcomeScreen from './pages/WelcomeScreen';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SetupPage from './pages/SetupPage';
import JobDetailPage from './pages/JobDetailPage';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MapPage from './pages/MapPage';
import DesktopNav from './components/DesktopNav';
import DesktopSidebar from './components/DesktopSidebar';
import DesktopRightPanel from './components/DesktopRightPanel';

function FlashBanner() {
  const { flashMsg, setFlashMsg } = useApp();
  useEffect(() => {
    if (!flashMsg) return;
    const t = setTimeout(() => setFlashMsg(null), 4000);
    return () => clearTimeout(t);
  }, [flashMsg, setFlashMsg]);
  if (!flashMsg) return null;
  return <div className="flash-banner">{flashMsg}</div>;
}

const FULLSCREEN_ROUTES = ['/verify-email', '/setup', '/welcome', '/reset-password', '/profile/edit'];

function AppContent() {
  const { user, pendingVerificationEmail, userType } = useApp();
  const location = useLocation();

  const isFullscreen = FULLSCREEN_ROUTES.some(r => location.pathname.startsWith(r));

  // Onboarding Schritt "E-Mail bestätigen": Code-Eingabe direkt nach der Registrierung
  if (pendingVerificationEmail) {
    return <EmailVerificationScreen email={pendingVerificationEmail} />;
  }

  // Setup-Gate: wer das Setup noch nicht abgeschlossen hat, landet immer dort —
  // auch nach einem späteren Login
  if (user && user.setup_complete === false
      && !location.pathname.startsWith('/setup')
      && !location.pathname.startsWith('/welcome')) {
    return <Navigate to="/setup" replace />;
  }

  const isEmployer = userType === 'employer';

  return (
    <div className="app-shell">
      <AuthModal />
      <FlashBanner />
      {!isFullscreen && <DesktopNav />}
      <div className="d-body">
        {!isFullscreen && <DesktopSidebar />}
        <div className={`app-content${isFullscreen ? ' app-content--fullscreen' : ''}`}>
          <Routes>
            <Route path="/" element={isEmployer ? <InsertePage /> : <ForYouPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/talents" element={<TalentePage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* "Erzähl uns von dir", "Über mich", "Wünsche": dieselben Screens wie im Setup */}
            <Route path="/profile/edit/:section" element={<SetupPage />} />
            <Route path="/post-job" element={<PostJobPage />} />
            <Route path="/post-job/:id" element={<PostJobPage />} />
            <Route path="/employers" element={<PostJobPage />} />
            <Route path="/jobs" element={<SearchPage />} />
            <Route path="/jobs/:id" element={<JobDetailPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/welcome" element={<WelcomeScreen />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="*" element={isEmployer ? <InsertePage /> : <ForYouPage />} />
          </Routes>
        </div>
        {!isFullscreen && <DesktopRightPanel />}
      </div>
      {!isFullscreen && <BottomNav />}
    </div>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(
    () => !sessionStorage.getItem('splashShown')
  );

  const handleSplashDone = useCallback(() => {
    sessionStorage.setItem('splashShown', '1');
    setShowSplash(false);
  }, []);

  return (
    <AppProvider>
      <Router>
        {showSplash && <SplashScreen onDone={handleSplashDone} />}
        <AppContent />
      </Router>
    </AppProvider>
  );
}

export default App;
